import { Head, Link, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { Heart, Music, Users, Disc, MapPin, Tag } from "lucide-react"

import type { PageProps } from "@/types/inertia"

type Counts = {
  bands:         number
  people:        number
  recordings:    number
  venues:        number
  record_labels: number
}

type RecentItem = {
  name:       string
  type:       string
  slug:       string
  updated_at: string
}

type Props = PageProps<{ counts: Counts; recently_updated: RecentItem[] }>

const ENTITY_CONFIG: Array<{
  key: keyof Counts
  label: string
  href: string
  icon: React.ComponentType<{ className?: string }>
}> = [
  { key: "bands",         label: "Bands",         href: "/bands",         icon: Music   },
  { key: "people",        label: "People",         href: "/people",        icon: Users   },
  { key: "recordings",    label: "Recordings",     href: "/recordings",    icon: Disc    },
  { key: "venues",        label: "Venues",         href: "/venues",        icon: MapPin  },
  { key: "record_labels", label: "Record Labels",  href: "/record_labels", icon: Tag     },
]

const TYPE_HREF: Record<string, string> = {
  Band:      "/bands",
  Person:    "/people",
  Recording: "/recordings",
  Venue:     "/venues",
}

function entityHref(item: RecentItem) {
  const base = TYPE_HREF[item.type] ?? "/"
  return `${base}/${item.slug}`
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins  = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days  = Math.floor(diff / 86400000)
  if (mins < 60)   return `${mins}m ago`
  if (hours < 24)  return `${hours}h ago`
  if (days < 30)   return `${days}d ago`
  return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
}

export default function Dashboard({ counts, recently_updated }: { counts: Counts; recently_updated: RecentItem[] }) {
  const { props } = usePage<Props>()
  const user   = props.current_user
  const adFree = props.ad_free

  const greeting = user?.name ? `Welcome back, ${user.name.split(" ")[0]}.` : "Welcome back."

  return (
    <>
      <Head title="Dashboard">
        <meta name="description" content="Your Australian Music Database dashboard." />
        <meta property="og:title" content="Dashboard" />
        <meta property="og:description" content="Your Australian Music Database dashboard." />
      </Head>
      <AppShell>
        <h1>{greeting}</h1>
        <p className="mt-1 text-ink-muted">Explore the database or pick up where you left off.</p>

        {/* ── Quick explore ──────────────────────────────────────────── */}
        <section className="mt-8">
          <h2 className="text-base mb-4">Explore</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {ENTITY_CONFIG.map(({ key, label, href, icon: Icon }) => (
              <Link
                key={key}
                href={href}
                className="flex flex-col items-center gap-2 rounded-xl border border-hairline bg-surface p-4 no-underline hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors group"
              >
                <Icon className="w-6 h-6 text-amber-500 group-hover:text-amber-600 transition-colors" />
                <span className="text-2xl font-semibold text-ink-display tabular-nums">
                  {counts[key].toLocaleString()}
                </span>
                <span className="text-xs text-ink-muted">{label}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* ── Recently updated ───────────────────────────────────────── */}
        {recently_updated.length > 0 && (
          <section className="mt-10">
            <h2 className="text-base mb-4">Recently updated</h2>
            <ul className="divide-y divide-hairline rounded-xl border border-hairline bg-surface overflow-hidden">
              {recently_updated.map((item, i) => (
                <li key={i}>
                  <Link
                    href={entityHref(item)}
                    className="flex items-center justify-between gap-4 px-4 py-3 no-underline hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
                  >
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-ink-display truncate block">{item.name}</span>
                      <span className="text-xs text-ink-muted">{item.type}</span>
                    </div>
                    <span className="text-xs text-ink-muted shrink-0">{timeAgo(item.updated_at)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ── Donate nudge ───────────────────────────────────────────── */}
        {!adFree && (
          <div className="mt-8 flex items-start gap-3 rounded-xl border border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-950/30 px-4 py-3.5 max-w-md">
            <Heart className="w-4 h-4 mt-0.5 text-amber-500 fill-current shrink-0" />
            <p className="text-sm text-amber-800 dark:text-amber-300">
              Enjoying the database?{" "}
              <Link href="/donate" className="font-medium underline hover:text-amber-600 dark:hover:text-amber-200">
                A small donation
              </Link>{" "}
              removes ads and helps keep it running.
            </p>
          </div>
        )}
      </AppShell>
    </>
  )
}
